import {useCallback, useEffect, useRef, useState} from 'react';

import {useFormContext} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {GooglePlacesType} from 'desktop/components/addressField/addressField.types';
import {AddressFormItemType, AddressFormType} from 'desktop/types/addressType';

import {useGetLocation} from 'hooks/location/location';

import {useSelectorRef} from 'hooks/useSelectorRef/useSelectorRef';
import {applyModifiers} from 'libs/googlePlacesModifier';
import {CountriesType} from 'types/locationType';

import {saveBillingAddress} from '../../store/reducers/deliveryStep/billingAddress/billingAddress';
import {saveShippingAddress} from '../../store/reducers/deliveryStep/shippingAddress/shippingAddress';
import {setShippingMethodsOnAutocomplete} from '../../store/reducers/sagaStep/sagaStep';
import {getAddressesSameness} from '../../store/selectors/deliveryStep/deliveryStep';

import {
    getAddressComponents,
    googlePlacesAssociations,
    GooglePlacesAssociationType,
} from './helpers';

/**
 * Hook for address form which map google address to form data & update it
 */
export const useMapAutocomplete = (target: 'shippingAddress' | 'billingAddress') => {
    const dispatch = useDispatch();
    const useThisAddressForBothAddressesRef = useSelectorRef(getAddressesSameness);
    const [countryData, setCountryData] = useState<null | {
        result: AddressFormItemType;
        components: {[key: string]: {shortName: string; longName: string}};
        countryCode: string;
    }>(null);

    const {getValues, setError, setValue} = useFormContext<AddressFormType>();
    const {countries = [], regions = {}} = useGetLocation(
        countryData?.countryCode || getValues()[target]?.country,
    );
    const locationRef = useRef({states: regions, validCountries: countries});

    const saveResults = useCallback(
        (result: AddressFormItemType) => {
            const fieldKeys: ('shippingAddress' | 'billingAddress')[] =
                useThisAddressForBothAddressesRef.current
                    ? ['billingAddress', 'shippingAddress']
                    : [target];

            const reduxAddress: Record<string, {error: boolean; value: string; success: boolean}> =
                {};
            Object.entries(result).forEach(([key, value]) => {
                fieldKeys.forEach(addressKey => {
                    setValue(`${addressKey}.${key as keyof AddressFormItemType}`, value);
                });
                reduxAddress[key] = {
                    error: false,
                    success: true,
                    value,
                };
            });
            fieldKeys.forEach(key => {
                if (key === 'shippingAddress') dispatch(saveShippingAddress(reduxAddress));
                else dispatch(saveBillingAddress(reduxAddress));
            });

            dispatch(setShippingMethodsOnAutocomplete());
        },
        [useThisAddressForBothAddressesRef, target, setValue, dispatch],
    );

    useEffect(() => {
        locationRef.current = {states: regions || {}, validCountries: countries || []};
    }, [regions, countries]);

    useEffect(() => {
        if (countryData && regions && regions[countryData?.countryCode]) {
            const {components, countryCode, result} = countryData;
            googlePlacesAssociations.forEach(
                ({fieldName, nameType, placeLabel}: GooglePlacesAssociationType) => {
                    result[fieldName] = fieldName === 'state' ? '0' : '';
                    const finalStates = regions?.[countryCode]?.length ? regions[countryCode] : [];

                    placeLabel.forEach((label: string) => {
                        if (components[label]) {
                            const modifiers = applyModifiers(fieldName, components[label], {
                                countries,
                                result,
                                states: finalStates,
                                type: nameType,
                            });
                            const addressFormItem = Object.entries(modifiers).reduce(
                                (acc, [key, data]) => ({
                                    ...acc,
                                    [key]: data.value,
                                }),
                                {} as AddressFormItemType,
                            );
                            Object.assign(result, addressFormItem);
                        }
                    });
                },
            );

            saveResults(result);

            setCountryData(null);
        }
    }, [countryData, regions]);

    return useCallback(
        // eslint-disable-next-line max-statements
        async (places: GooglePlacesType) => {
            const {states, validCountries} = locationRef.current;
            const components = getAddressComponents(places);
            const countryCode = components?.country?.shortName;

            const isCountryValid = validCountries?.find(
                (country: CountriesType): boolean => country.value === countryCode,
            );

            if (!isCountryValid) {
                setError(`${target}.address`, {
                    message: 'Sorry we are not shipping to this region at this time',
                    type: 'validate',
                });
                return;
            }

            const result = getValues()[target];
            const finalStates = states?.[countryCode]?.length ? states[countryCode] : null;

            if (finalStates) {
                googlePlacesAssociations.forEach(
                    ({fieldName, nameType, placeLabel}: GooglePlacesAssociationType) => {
                        result[fieldName] = fieldName === 'state' ? '0' : '';

                        placeLabel.forEach((label: string) => {
                            if (components[label]) {
                                const modifiers = applyModifiers(fieldName, components[label], {
                                    countries: validCountries,
                                    result,
                                    states: finalStates,
                                    type: nameType,
                                });
                                const addressFormItem = Object.entries(modifiers).reduce(
                                    (acc, [key, data]) => ({
                                        ...acc,
                                        [key]: data.value,
                                    }),
                                    {} as AddressFormItemType,
                                );
                                Object.assign(result, addressFormItem);
                            }
                        });
                    },
                );

                saveResults(result);
            } else {
                setCountryData({
                    components,
                    countryCode,
                    result,
                });
            }
        },
        [target, saveResults, setError, getValues],
    );
};
